<template>
    <div class="hailuo-model-page">
        <!-- Video Generator Section -->
        <section ref="generatorSection" class="video-generator-section">
            <div class="container">
                <div class="generator-container">
                    <!-- Generator Header -->
                    <div class="generator-header">
                        <div class="generator-title">
                            <h1>{{ t('hailuo.generatorTitle') }}</h1>
                            <p class="generator-description" v-html="t('hailuo.generatorDescription')"></p>
                            <div class="rainbow-bar"></div>
                        </div>
                    </div>

                    <!-- Generator Interface -->
                    <VideoGenerator model-name="Hailuo" :tabs="tabs" redirect-url="/ai-video" @generate="handleGenerate"
                        model-key="hailuo-2-3">
                        <template #model-logo>
                            <div class="hailuo-logo">H</div>
                        </template>
                    </VideoGenerator>
                </div>
            </div>
        </section>

        <!-- Core Features Section -->
        <section class="core-features-section relative z-10 !bg-transparent">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('hailuo.coreFeaturesTitle') }}
                    </h2>
                </div>

                <div class="features-showcase-container">
                    <!-- Feature 1: 准确遵循指令 -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{
                                        t(
                                            'hailuo.accurateInstructionFollowingTitle'
                                        )
                                    }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'hailuo.accurateInstructionFollowingDescription'
                                        )
                                    }}
                                </p>
                            </div>
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.accurateInstructionFollowing"
                                    :poster="getResourceThumbnail(demoVideos.accurateInstructionFollowing)"
                                    :title="t('hailuo.accurateInstructionFollowingTitle')"
                                    :description="t('hailuo.accurateInstructionFollowingDescription')"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Feature 2: 极端物理和动态运动 -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.extremePhysicsMotion"
                                    :poster="getResourceThumbnail(demoVideos.extremePhysicsMotion)"
                                    :title="t('hailuo.extremePhysicsMotionTitle')"
                                    :description="t('hailuo.extremePhysicsMotionDescription')"
                                />
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('hailuo.extremePhysicsMotionTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'hailuo.extremePhysicsMotionDescription'
                                        )
                                    }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Feature 3: 清晰的原生 1080p 输出 -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('hailuo.native1080pOutputTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t('hailuo.native1080pOutputDescription')
                                    }}
                                </p>
                            </div>
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.native1080pOutput"
                                    :poster="getResourceThumbnail(demoVideos.native1080pOutput)"
                                    :title="t('hailuo.native1080pOutputTitle')"
                                    :description="t('hailuo.native1080pOutputDescription')"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Feature 4: 无与伦比的成本效益 -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.costEffective"
                                    :poster="getResourceThumbnail(demoVideos.costEffective)"
                                    :title="t('hailuo.costEffectiveTitle')"
                                    :description="t('hailuo.costEffectiveDescription')"
                                />
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('hailuo.costEffectiveTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{ t('hailuo.costEffectiveDescription') }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- How to Use Section -->
        <section class="how-to-use-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('hailuo.howToUseTitle') }}
                    </h2>
                </div>

                <div class="steps-container">
                    <div class="step-item">
                        <div class="step-number">1</div>
                        <div class="step-content">
                            <h3 class="step-title">
                                {{ t('hailuo.step1Title') }}
                            </h3>
                            <p class="step-description" v-html="t('hailuo.step1Description')"></p>
                        </div>
                    </div>

                    <div class="step-item">
                        <div class="step-number">2</div>
                        <div class="step-content">
                            <h3 class="step-title">
                                {{ t('hailuo.step2Title') }}
                            </h3>
                            <p class="step-description">
                                {{ t('hailuo.step2Description') }}
                            </p>
                        </div>
                    </div>

                    <div class="step-item">
                        <div class="step-number">3</div>
                        <div class="step-content">
                            <h3 class="step-title">
                                {{ t('hailuo.step3Title') }}
                            </h3>
                            <p class="step-description">
                                {{ t('hailuo.step3Description') }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- YouTube Videos Section -->
        <section class="youtube-videos-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('hailuo.youtubeTitle') }}
                    </h2>
                </div>

                <div class="youtube-carousel">
                    <button class="carousel-nav-btn carousel-nav-left" @click="previousVideo"
                        :aria-label="t('common.previous')">
                        <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd"
                                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                clip-rule="evenodd" />
                        </svg>
                    </button>

                    <div class="videos-wrapper">
                        <div class="videos-scroll" :style="{
                            transform: `translateX(-${currentVideoIndex * (100 / 3)}%)`,
                        }">
                            <div v-for="(video, index) in youtubeVideos" :key="index" class="video-iframe-container">
                                <iframe :src="video.embedUrl" :title="video.title" class="video-iframe" frameborder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowfullscreen></iframe>
                            </div>
                        </div>
                    </div>

                    <button class="carousel-nav-btn carousel-nav-right" @click="nextVideo"
                        :aria-label="t('common.next')">
                        <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd"
                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                clip-rule="evenodd" />
                        </svg>
                    </button>
                </div>
            </div>
        </section>

        <!-- X Posts Section -->
        <TwitterPosts :title="t('hailuo.xPostsTitle')" :tweet-urls="twitterTweetUrls" />

        <!-- Explore Other Models Section -->
        <section class="explore-models-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('hailuo.exploreModelsTitle') }}
                    </h2>
                </div>

                <div class="models-grid">
                    <NuxtLink to="/video/hailuo-23" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/2b9d9924fb8b25932b804c7fe99d792d.png"
                                :alt="t('hailuo.hailuo23Title')" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('hailuo.hailuo23Title') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/video/sora/sora-2" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/7ac1a1457b1e788829a2da1d178879bb.png"
                                :alt="t('hailuo.sora2Title')" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('hailuo.sora2Title') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/video/wan-ai" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/88fca6eaf44c1b7c1e02caa0a061fe11.png"
                                :alt="t('hailuo.wanTitle')" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('hailuo.wanTitle') }}
                        </h3>
                    </NuxtLink>
                </div>
            </div>
        </section>

        <!-- FAQ Section -->
        <section class="faq-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">{{ t('hailuo.faqTitle') }}</h2>
                </div>

                <div class="faq-list">
                    <div v-for="(faq, index) in faqData" :key="index" class="faq-item"
                        :class="{ active: activeFaqIndex === index }">
                        <div class="faq-question" @click="toggleFaq(index)">
                            <!-- <span class="faq-icon">></span> -->
                            <span class="faq-text">{{ faq.question }}</span>
                        </div>
                        <div class="faq-answer">
                            <p>{{ faq.answer }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- CTA Section -->
        <section class="cta-section">
            <div class="container">
                <div class="cta-content">
                    <!-- 微鲤AI Logo -->
                    <div class="cta-logo">
                        <div class="logo-icon logo-image">
                            <!-- <img src="/weryai_48.png" alt="微鲤AI" class="logo-image" /> -->
                            <img src="https://static.weryai.com/growth/image/4608e0a35205ff929dba2ea3c51f6e62.webp"
                                alt="微鲤AI logo" class="logo-image" />
                        </div>
                    </div>

                    <!-- Main Content -->
                    <div class="cta-header">
                        <h2 class="cta-title">{{ t('hailuo.ctaTitle') }}</h2>
                        <p class="cta-description">
                            {{ t('hailuo.ctaDescription') }}
                        </p>
                    </div>

                    <!-- CTA Button -->
                    <div class="cta-actions">
                        <button @click="scrollToGenerator" class="cta-primary-btn">
                            {{ t('hailuo.ctaButton') }}
                        </button>

                        <p class="cta-disclaimer">
                            {{ t('hailuo.ctaDisclaimer') }}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useHead, useRequestURL } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { useHowToSeo } from '~/composables/useHowToSeo'
import { useVideoSeo } from '~/composables/useVideoSeo'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { analyticsEvent } from '~/utils/log-event-func'
import { useSettingVideoSEO } from '~/composables/useSettingVideoSEO'
import { getResourceThumbnail } from '~/utils/common'

const { t } = useI18n()
const requestUrl = useRequestURL()
const requestUrlHref = requestUrl.href

const demoVideos = {
    accurateInstructionFollowing:
        'https://static.weryai.com/growth/video/10be23308d719e387d481498124736e1.mp4',
    extremePhysicsMotion:
        'https://static.weryai.com/growth/video/3dc3c58c9743b837695977d14a86f33d.mp4',
    native1080pOutput:
        'https://static.weryai.com/growth/video/47986b7b1f65941a996da83676092949.mp4',
    costEffective:
        'https://static.weryai.com/growth/video/87ee3f586d0f04114d1f0d57ea88de04.mp4',
    videoObjectMain:
        'https://static.weryai.com/growth/video/weryai_0bc946f5460c4dcc7a9af9e629c5923a.mp4',
} as const

definePageMeta({
    layout: 'website',
})

useMultilingualSeo({ pageType: 'hailuo_ai' })

// BreadcrumbList SEO
useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: 'Home',
                            item: 'https://www.weryai.com.cn/',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('hailuo.generatorTitle'),
                            item: requestUrlHref,
                        },
                    ],
                })
            ),
        },
    ],
})

onMounted(() => {
    analyticsEvent('view_model_page_common', {
        model_name: 'hailuo_ai',
        type: 'video',
    })
})

// HowTo Steps for SEO
const howToUseSteps = computed(() => [
    {
        title: t('hailuo.step1Title'),
        description: t('hailuo.step1Description'),
    },
    {
        title: t('hailuo.step2Title'),
        description: t('hailuo.step2Description'),
    },
    {
        title: t('hailuo.step3Title'),
        description: t('hailuo.step3Description'),
    },
])

// HowTo SEO - 注入结构化数据
useHowToSeo(
    computed(() => t('hailuo.howToUseTitle')),
    howToUseSteps
)

// Ref for generator section
const generatorSection = ref<HTMLElement | null>(null)

// Scroll to generator function
const scrollToGenerator = () => {
    if (generatorSection.value) {
        generatorSection.value.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }
}

// SEO Meta Data
useHead({
    title: computed(() => t('hailuo.pageTitle') + ' | 微鲤AI'),
    meta: [
        {
            name: 'description',
            content: computed(() => t('hailuo.pageDescription')),
        },
        {
            name: 'keywords',
            content: computed(() => t('hailuo.pageKeywords')),
        },
        {
            property: 'og:title',
            content: computed(() => t('hailuo.pageTitle')),
        },
        {
            property: 'og:description',
            content: computed(() => t('hailuo.pageDescription')),
        },
        {
            property: 'og:type',
            content: 'website',
        },
        {
            name: 'twitter:card',
            content: 'summary_large_image',
        },
        {
            name: 'twitter:title',
            content: computed(() => t('hailuo.pageTitle')),
        },
        {
            name: 'twitter:description',
            content: computed(() => t('hailuo.pageDescription')),
        },
    ],
    link: [
        {
            key: 'canonical',
            rel: 'canonical',
            href: 'https://www.weryai.com.cn/video/hailuo-ai',
        },
    ],
})

// Structured Data for SEO
useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'SoftwareApplication',
                name: 'Hailuo 02 AI 视频生成器（MiniMax）',
                description:
                    '创新的AI视频生成平台，专注于智能视频创作和内容优化，支持风格转换和实时处理',
                applicationCategory: 'MultimediaApplication',
                operatingSystem: 'Web',
                offers: {
                    '@type': 'Offer',
                    price: '0',
                    priceCurrency: 'USD',
                },
                provider: {
                    '@type': 'Organization',
                    name: '微鲤AI',
                    url: 'https://www.weryai.com.cn',
                },
                featureList: [
                    '智能视频创作',
                    '内容优化',
                    '风格转换',
                    '实时处理',
                ],
            }),
        },
    ],
})

// Tabs configuration
const tabs = computed(() => [
    { name: t('hailuo.tabImageToVideo'), external: false },
    { name: t('hailuo.tabTextToVideo'), external: false },
])

// Methods
const handleGenerate = (data: {
    type: 'image' | 'text'
    image?: File
    prompt?: string
}) => {
    // 组件内部已经处理了跳转逻辑
}

// YouTube Videos Data
const currentVideoIndex = ref(0)

const youtubeVideos = ref([
    {
        title: 'Hailuo AI - Complete Overview',
        embedUrl: 'https://www.youtube.com/embed/J18idUz87LA',
    },
    {
        title: 'Hailuo AI Tutorial Guide',
        embedUrl: 'https://www.youtube.com/embed/Fuy9t1TFcHs',
    },
    {
        title: 'Hailuo AI Features Explained',
        embedUrl: 'https://www.youtube.com/embed/de9bJw2Dtp4',
    },
    {
        title: 'Getting Started with Hailuo AI',
        embedUrl: 'https://www.youtube.com/embed/hTExP3xZsCM',
    },
    {
        title: 'Hailuo AI Advanced Tips',
        embedUrl: 'https://www.youtube.com/embed/sUXFBQjjkgA',
    },
])

// Video SEO - 注入 VideoObject 结构化数据
useVideoSeo(youtubeVideos.value)

// Twitter Tweet URLs Data
const twitterTweetUrls = [
    'https://twitter.com/WuxiaRocks/status/1935124765599293864',
    'https://twitter.com/JamianGerard/status/1935074253143163106',
    'https://twitter.com/creacas/status/1934975993594384713',
    'https://twitter.com/JamianGerard/status/1935141363449794626',
    'https://twitter.com/alexgnewmedia/status/1935002400227520554',
    'https://twitter.com/CharaspowerAI/status/1935114159051706445',
    'https://twitter.com/EccentrismArt/status/1935066841090814400',
    'https://twitter.com/h_ashizawaJP/status/1935060243937116438',
    'https://twitter.com/GrimfelOfficial/status/1935045550392414542',
    'https://twitter.com/CtrlMuse/status/1935021156429611210',
    'https://twitter.com/chrisfirst/status/1935040948850888847',
    'https://twitter.com/StevieMac03/status/1934984180460347855',
]

// Carousel Methods
const nextVideo = () => {
    // Maximum 3 visible videos, so max index is 2 (3rd video)
    const maxVisibleIndex = Math.min(2, youtubeVideos.value.length - 1)
    if (currentVideoIndex.value >= maxVisibleIndex) {
        currentVideoIndex.value = 0 // Back to first video
    } else {
        currentVideoIndex.value++
    }
}

const previousVideo = () => {
    if (currentVideoIndex.value <= 0) {
        // Back to last visible video position
        const maxVisibleIndex = Math.min(2, youtubeVideos.value.length - 1)
        currentVideoIndex.value = maxVisibleIndex
    } else {
        currentVideoIndex.value--
    }
}

// FAQ Data
const faqData = computed(() => [
    {
        question: t('hailuo.faq1Question'),
        answer: t('hailuo.faq1Answer'),
    },
    {
        question: t('hailuo.faq2Question'),
        answer: t('hailuo.faq2Answer'),
    },
    {
        question: t('hailuo.faq3Question'),
        answer: t('hailuo.faq3Answer'),
    },
    {
        question: t('hailuo.faq4Question'),
        answer: t('hailuo.faq4Answer'),
    },
    {
        question: t('hailuo.faq5Question'),
        answer: t('hailuo.faq5Answer'),
    },
])

// FAQ Interactive Functions
const activeFaqIndex = ref<number | null>(null)

const toggleFaq = (index: number) => {
    if (activeFaqIndex.value === index) {
        activeFaqIndex.value = null
    } else {
        activeFaqIndex.value = index
    }
}

useSettingVideoSEO({
    key: 'hailuoai-videoobject',
    title: t('hailuo.generatorTitle'),
    description: t('hailuo.pageDescription'),
    contentUrl: demoVideos.videoObjectMain,
    thumbnailUrl: 'https://static.weryai.com/growth/image/weryai_5f8706d3d8f4a4a196595d6b98b03ad6.png',
})
</script>

<style lang="less" scoped>
@import '~/assets/css/howToUse.css';
</style>
